import type { ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { useAuthStore } from './stores/auth.store'
import { PrivateRoute } from './components/auth/PrivateRoute'
import { MainLayout } from './components/layout/MainLayout'
import { Login } from './pages/Login'
import { Register } from './pages/Register'
import { ForgotPassword } from './pages/ForgotPassword'
import { ResetPassword } from './pages/ResetPassword'
import { VerifyEmail } from './pages/VerifyEmail'
import { PendingVerification } from './pages/PendingVerification'
import { SettingsPage } from './pages/settings/SettingsPage'
import { StorageSettings } from './pages/settings/StorageSettings'
import { BackupPage } from './pages/settings/BackupPage'
import { Dashboard } from './pages/Dashboard'
import { ProjectDetail } from './pages/projects/ProjectDetail'
import { ProjectActivities } from './pages/projects/ProjectActivities'
import { ProjectClose } from './pages/projects/ProjectClose'
import { ActivityReviewPage } from './pages/projects/ActivityReviewPage'
import { LevantamentoReportPage } from './pages/projects/LevantamentoReportPage'
import { ProjectLevantamento } from './pages/projects/ProjectLevantamento'
import { Contractors } from './pages/Contractors'
import { ContractorDetail } from './pages/contractors/ContractorDetail'
import { Brokers } from './pages/Brokers'
import { BrokerDetail } from './pages/brokers/BrokerDetail'
import { RegisterBroker } from './pages/RegisterBroker'
import { ActivityTemplates } from './pages/settings/ActivityTemplates'
import { ActivityTemplateEditor } from './pages/settings/ActivityTemplateEditor'
import { Users } from './pages/Users'
import { Profile } from './pages/Profile'
import { Financial } from './pages/Financial'
import { Units } from './pages/Units'
import { Performance } from './pages/Performance'
import { Suppliers } from './pages/Suppliers'
import { SupplierDetail } from './pages/suppliers/SupplierDetail'
import { Buyers } from './pages/Buyers'
import { BuyerDetail } from './pages/buyers/BuyerDetail'
import { Investors } from './pages/Investors'
import { InvestorDetail } from './pages/investors/InvestorDetail'
import { Assets } from './pages/Assets'
import { AssetDetail } from './pages/assets/AssetDetail'
import { PurchaseOrders } from './pages/PurchaseOrders'
import { Invoices } from './pages/Invoices'
import { InvoiceDetail } from './pages/invoices/InvoiceDetail'
import { Colaboradores } from './pages/Colaboradores'
import { ColaboradorDetail } from './pages/colaboradores/ColaboradorDetail'
import { Quotations } from './pages/Quotations'
import { QuotationDetail } from './pages/quotations/QuotationDetail'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/register-contractor" element={<Navigate to="/register" replace />} />
          <Route path="/register-broker" element={<RegisterBroker />} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/pending-verification" element={<PendingVerification />} />
          <Route path="/pending-approval" element={<PendingApproval />} />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <MainLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="projects/:id" element={<ProjectDetail />} />
            <Route path="projects/:id/activities" element={<ProjectActivities />} />
            <Route path="projects/:id/levantamento" element={<ProjectLevantamento />} />
            <Route path="levantamento" element={<ProjectLevantamento />} />
            <Route path="projects/:id/review-activities" element={<ActivityReviewPage />} />
            <Route path="projects/:id/levantamento-report" element={<LevantamentoReportPage />} />
            <Route path="projects/:id/close" element={<ProjectClose />} />
            <Route path="units" element={<Units />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="suppliers/:id" element={<SupplierDetail />} />
            <Route path="buyers" element={<Buyers />} />
            <Route path="buyers/:id" element={<BuyerDetail />} />
            <Route path="investors" element={<Investors />} />
            <Route path="investors/:id" element={<InvestorDetail />} />
            <Route path="contractors" element={<Contractors />} />
            <Route path="contractors/:id" element={<ContractorDetail />} />
            <Route path="brokers" element={<Brokers />} />
            <Route path="brokers/:id" element={<BrokerDetail />} />
            <Route path="assets" element={<Assets />} />
            <Route path="assets/:id" element={<AssetDetail />} />
            <Route path="purchases" element={<PurchaseOrders />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/:id" element={<InvoiceDetail />} />
            <Route path="colaboradores" element={<Colaboradores />} />
            <Route path="colaboradores/:id" element={<ColaboradorDetail />} />
            <Route path="quotations" element={<Quotations />} />
            <Route path="quotations/:id" element={<QuotationDetail />} />
            <Route path="financial" element={<Financial />} />
            <Route path="contracts" element={<ComingSoon title="Contratos" />} />
            <Route path="reports" element={<ComingSoon title="Relatórios" />} />
            <Route path="profile" element={<Profile />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/storage" element={<StorageSettings />} />
            <Route path="settings/backup" element={<BackupPage />} />
            <Route path="settings/planejamentos" element={<ActivityTemplates />} />
            <Route path="settings/planejamentos/:id" element={<ActivityTemplateEditor />} />
            <Route path="users" element={<Users />} />
            <Route path="performance" element={<Performance />} />
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </QueryClientProvider>
  )
}

// Public route wrapper - redirects to dashboard if already authenticated
function PublicRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthStore()

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// Pending approval page
function PendingApproval() {
  const { user, clearAuth } = useAuthStore()
  const queryClient = useQueryClient()

  const handleLogout = () => {
    queryClient.clear()
    clearAuth()
    window.location.href = '/login'
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-6">
      <div className="w-full max-w-md text-center">
        <div className="rounded-lg bg-white p-8 shadow-sm border border-neutral-200">
          <div className="mx-auto h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
            <svg className="h-8 w-8 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-neutral-900">Aguardando Aprovação</h2>
          <p className="mt-3 text-neutral-600">
            Olá{user?.name ? `, ${user.name}` : ''}! Seu cadastro foi recebido e está aguardando aprovação da empresa.
          </p>
          <p className="mt-2 text-sm text-neutral-500">
            Você receberá acesso assim que um administrador aprovar sua conta.
          </p>
          <button
            onClick={handleLogout}
            className="mt-6 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Sair e voltar ao login
          </button>
        </div>
      </div>
    </div>
  )
}

// Coming soon placeholder
function ComingSoon({ title }: { title: string }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-neutral-900">{title}</h1>
        <p className="mt-4 text-neutral-600">Esta funcionalidade será implementada em breve.</p>
        <p className="mt-2 text-sm text-neutral-500">Fase 3 em diante</p>
      </div>
    </div>
  )
}

export default App
